import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowRight, Zap, Shield, Scale, Gavel, BellRing, BookOpen, HandHeart, Building2, BarChart3, MessageSquare, Landmark, GraduationCap } from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";
import { useEffect, useState } from "react";

const PLAN_ICONS = { standard: Zap, plus: Shield, professional: Scale, elite: Sparkles };
const ADDON_ICONS = {
  legal_templates: Gavel, escalation: Scale, reminders: BellRing,
  ai_saint: Sparkles, state_laws: BookOpen, amendment_advisor: HandHeart,
  financial_reports: BarChart3, community_forum: MessageSquare,
  elections_kit: Landmark, training_kits: GraduationCap,
};

export default function Pricing() {
  const [data, setData] = useState({ plans: [], addons: [] });
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const price = (p) => cycle === "yearly" ? p.yearly : p.monthly;
  const suffix = cycle === "yearly" ? "/yr" : "/mo";

  return (
    <div className="min-h-screen paper-texture text-foreground">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
        <div className="flex items-center justify-end gap-3 mb-2">
          <Link to="/login" className="text-sm font-medium text-foreground/85 hover:text-gold transition-colors" data-testid="pricing-login-link">
            Log in
          </Link>
          <Link to="/register" className="text-sm font-medium bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/85 transition-colors glow-purple" data-testid="pricing-register-link">
            Get started
          </Link>
        </div>
        <Link to="/" className="flex flex-col items-center justify-center">
          <CommunityIQLogo className="w-full max-w-[900px] h-auto" />
          <p className="text-lg text-gold italic text-center mt-3 max-w-2xl" data-testid="hero-tagline">
            &ldquo;Stronger communities weren&rsquo;t built alone… it takes a village.&rdquo;
          </p>
        </Link>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-12 text-center">
        <div className="inline-flex items-center gap-2 bg-accent/60 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border border-gold">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="text-gold">14-day free trial on every plan. No credit card commitment.</span>
        </div>
        <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight mb-4 leading-[1.05]">
          <span className="royal-gradient-text">Choose your tier.</span>
          <br />
          <span className="text-gold">Try any plan free for 14 days.</span>
        </h1>
        <p className="no-gold text-foreground/75 max-w-2xl mx-auto text-lg mb-8">
          Start on Standard, Plus, Professional, or Elite — every plan (and every add-on) ships with a
          <span className="text-gold font-semibold"> 14-day free trial</span>. Cancel anytime before the trial ends.
        </p>

        {/* Cycle toggle */}
        <div className="inline-flex items-center gap-1 bg-card border border-border rounded-full p-1" data-testid="cycle-toggle">
          {["monthly", "yearly"].map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                cycle === c ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-gold"
              }`}
              data-testid={`cycle-${c}`}
            >
              {c === "monthly" ? "Monthly" : "Yearly · save 2 mo"}
            </button>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Zap;
            return (
              <div
                key={p.id}
                className={`hoa-card p-7 flex flex-col relative ${p.highlighted ? "royal-ring glow-purple border-gold" : ""}`}
                data-testid={`plan-${p.id}`}
              >
                {p.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-secondary-foreground text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gold" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="no-gold text-lg font-semibold">{p.name}</div>
                    <div className="text-lg text-muted-foreground">{p.tagline}</div>
                  </div>
                </div>
                <div className="mb-5">
                  <span className="text-4xl font-semibold text-gold font-mono" data-testid={`plan-price-${p.id}`}>
                    ${price(p)}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">{p.monthly === 0 ? "forever" : suffix}</span>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`plan-cta-${p.id}`}
                  className={`inline-flex items-center justify-center gap-2 rounded-full py-3 font-semibold transition-all ${
                    p.highlighted
                      ? "bg-gold text-secondary-foreground hover:brightness-110 glow-gold"
                      : "bg-primary text-primary-foreground hover:bg-primary/85"
                  }`}
                >
                  Start 14-day free trial <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Nonprofit */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="hoa-card royal-ring p-8 md:p-10" data-testid="wo-comparison-table">
          <div className="text-xs uppercase tracking-widest text-gold-soft mb-2">Work-order workflow · plan comparison</div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-2">
            <span className="royal-gradient-text">One work order</span> can pay for your plan.
          </h3>
          <p className="no-gold text-foreground/75 max-w-2xl mb-6">
            AI Saint co-pilots the entire work-order lifecycle. Higher tiers unlock the automation that saves the most money & board hours.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-semibold text-foreground/85">Capability</th>
                  <th className="text-center py-3 px-2 font-semibold text-foreground/70">Standard</th>
                  <th className="text-center py-3 px-2 font-semibold text-foreground/85">Plus</th>
                  <th className="text-center py-3 px-2 font-semibold text-gold">Professional</th>
                  <th className="text-center py-3 px-2 font-semibold text-gold">Elite</th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                {[
                  ["Work order intake · photos · 12-step checklist",              true, true, true, true],
                  ["Assign committee & director · start/end dates",               true, true, true, true],
                  ["AI Saint scope-of-work (SoW · insurance · indemnity)",        false, true, true, true],
                  ["Bid intake · 3-bid minimum tracker · document uploads",       true, true, true, true],
                  ["AI bid comparison · best-value + technically-acceptable",     false, false, true, true],
                  ["Auto contractor agreement + attachments checklist",           false, false, false, true],
                  ["Live voting authentications",                                 false, false, true, true],
                  ["Real-time results on election day",                           false, false, true, true],
                  ["Push notices to all owners",                                  false, false, true, true],
                  ["Pay-app tracker · completion meter",                          true, true, true, true],
                  ["Walkthrough · warranty vault · closeout",                     true, true, true, true],
                  ["Delinquency dashboard · AI notices · printable PDF",          true, true, true, true],
                ].map(([label, ...cells]) => (
                  <tr key={label} className="border-b border-border/40">
                    <td className="py-3 pr-4">{label}</td>
                    {cells.map((v, i) => (
                      <td key={`${label}-${i}`} className="py-3 px-2 text-center">
                        {v ? <Check className="w-4 h-4 text-gold inline-block" /> : <span className="text-muted-foreground/40">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/30 text-sm flex flex-col md:flex-row items-start md:items-center gap-3">
            <Sparkles className="w-5 h-5 text-gold flex-shrink-0" />
            <p className="text-foreground/85 flex-1">
              <span className="font-semibold text-gold">Elite ROI:</span> A single contract drafted by our <span className="text-gold font-semibold">self-performing management with AI-assist AI Saint</span> typically replaces <span className="text-gold font-semibold">~$2,400</span> in legal fees. AI bid comparison catches non-responsive quotes that boards otherwise lose <span className="text-gold font-semibold">10–25%</span> to in change orders. <span className="italic">One project · one contract · Elite covered for the year.</span>
            </p>
            <Link to="/register" className="rounded-full bg-gold text-secondary-foreground px-5 py-2.5 text-sm font-semibold hover:brightness-110 whitespace-nowrap" data-testid="wo-compare-cta">
              Start Elite trial
            </Link>
          </div>
        </div>
      </section>

      {/* Nonprofit */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="hoa-card royal-ring p-8 md:p-10 flex flex-col md:flex-row items-start gap-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
          <div className="relative w-14 h-14 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center flex-shrink-0">
            <Building2 className="w-7 h-7" />
          </div>
          <div className="relative flex-1">
            <h3 className="text-2xl font-semibold mb-2">Nonprofit HOA? Get 20% off yearly.</h3>
            <p className="no-gold text-foreground/80 mb-4">
              Communities incorporated as nonprofit associations save 20% on any yearly subscription — including add-ons.
              Verify your EIN inside the platform and the discount applies automatically at renewal.
            </p>
            <Link
              to="/register"
              data-testid="nonprofit-cta"
              className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all"
            >
              Claim nonprofit discount <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="max-w-2xl mb-10">
          <p className="text-sm uppercase tracking-widest text-gold-soft font-medium mb-3">Add-ons · always evolving</p>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            <span className="royal-gradient-text">Power up when you need it.</span>
          </h2>
          <p className="no-gold text-foreground/75 mt-4">
            Every add-on ships with a 14-day free trial. We keep adding more — state-law libraries, amendment advisors,
            AI-powered reviews. Your board grows with the platform.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.addons.map((a) => {
            const Icon = ADDON_ICONS[a.id] || Zap;
            return (
              <div key={a.id} className="hoa-card p-6 flex flex-col" data-testid={`addon-${a.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gold" strokeWidth={2.2} />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-semibold text-gold font-mono">${a.price}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">/mo · {a.trial_days}d trial</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold-soft mb-1">{a.category}</div>
                <h3 className="text-lg font-semibold mb-2">{a.name}</h3>
                <p className="no-gold text-lg text-foreground/75 mb-4 leading-relaxed">{a.tagline}</p>
                <ul className="space-y-1.5 mb-5 flex-1">
                  {a.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-foreground/80">
                      <Check className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold border border-gold text-gold hover:bg-[hsl(var(--gold)/0.08)] transition-colors"
                  data-testid={`addon-cta-${a.id}`}
                >
                  Start free trial <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 text-center">
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          <span className="royal-gradient-text">Beautiful communities</span> deserve beautiful tools.
        </h2>
        <p className="no-gold text-foreground/75 max-w-2xl mx-auto mb-8">
          14-day free trial on every plan. Cancel anytime before the trial ends.
        </p>
        <Link
          to="/register"
          data-testid="pricing-final-cta"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full font-medium hover:bg-primary/85 transition-colors glow-purple"
        >
          Start your 14-day free trial <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} CommunityIQ · Communities beautifully managed.</p>
          <p className="text-xs">Demo: admin@hoa.com / admin123 · board@hoa.com / board123 · owner@hoa.com / owner123</p>
        </div>
      </footer>
    </div>
  );
}
